import 'react-international-phone/style.css';

import {
	TextFieldProps,
	InputAdornment,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import {
	CountryData,
	CountryIso2,
	defaultCountries,
	FlagImage,
	parseCountry,
	usePhoneInput,
} from 'react-international-phone';
import styles from './MuiPhoneInput.module.scss';

export interface MuiPhoneProps
	extends Omit<TextFieldProps, 'error' | 'onChange'> {
	value: string;
	countries?: CountryData[1][];
	onChange: ( phone: string ) => void;
	errorText?: string;
	/** When provided, keeps the selected country across re-renders (e.g. after save). Pass back the value from onCountryChange to persist. */
	country?: CountryIso2;
	setCountry?: ( country: CountryIso2 ) => void;
}

/* CountryData is a tuple...which is kind of a bummer
country[ 1 ] is the "iso" country code, so we can use that for filtering */

const MuiPhoneInput: React.FC<MuiPhoneProps> = ( {
	value,
	onChange,
	countries,
	errorText,
	country: countryProp,
	setCountry: setCountryProp,
	...restProps
} ) => {
	const allowedCountries = countries
		? defaultCountries.filter( ( country ) => countries.includes( country[ 1 ] ) )
		: defaultCountries;

	const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
		usePhoneInput( {
			defaultCountry: countryProp,
			value,
			countries: allowedCountries,
			disableCountryGuess: true,
			onChange: ( data ) => onChange?.( data.phone === '+1' ? '' : data.phone ),
		} );

	useEffect( () => {
		if ( countryProp && countryProp !== country?.iso2 ) {
			setCountry( countryProp, { focusOnInput: false } );
		}
	}, [
		country?.iso2,
		countryProp,
		setCountry
	] );

	return (
		<TextField
			variant='outlined'
			label='Phone number'
			color='primary'
			placeholder='Phone number'
			value={ inputValue }
			onChange={ handlePhoneValueChange }
			type='tel'
			inputRef={ inputRef }
			error={ !!errorText }
			helperText={ errorText }
			slotProps={ {
				...restProps?.slotProps,
				input: {
					...restProps?.slotProps?.input,
					startAdornment: (
						<InputAdornment position='start' className={ styles.flagContainer }>
							<Select
								className={ styles.flagSelect }
								disabled={ restProps.disabled }
								MenuProps={ {
									anchorOrigin: {
										vertical: 'bottom',
										horizontal: 'left',
									},
									transformOrigin: {
										vertical: 'top',
										horizontal: 'left',
									},
								} }
								value={ country?.iso2 }
								onChange={ ( e ) => {
									const iso2 = e.target.value as CountryIso2;
									setCountry( iso2, { focusOnInput: false } );
									setCountryProp?.( iso2 );
								} }
								renderValue={ ( value ) => (
									<FlagImage iso2={ value } className={ styles.flag } />
								) }
							>
								{ allowedCountries.map( ( c ) => {
									const country = parseCountry( c );
									return (
										<MenuItem key={ country.iso2 } value={ country.iso2 }>
											<FlagImage
												iso2={ country.iso2 }
												className={ styles.flagImage }
											/>
											<Typography className={ styles.countryName }>
												{ country.name }
											</Typography>
											<Typography color='gray'>+{ country.dialCode }</Typography>
										</MenuItem>
									);
								} ) }
							</Select>
						</InputAdornment>
					),
				},
			} }
			{ ...restProps }
		/>
	);
};

export default MuiPhoneInput;
